import { AppAlertDialog } from '../../../../shared/general';
import { FormElementDynamicInputTyped } from '../../../../shared/form-elements';
import { SustainabilityScanFormFields } from '../types';
import { Button } from '@chakra-ui/react';
import { getDefaultValues } from '../utils';
import { isValidZipcode } from '../../../../../utils/validations';
import { validationMessages } from '../../../../shared/validation/validation-messages';
import React, { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useSustainabilityScanFormFieldsConfig } from '../config';
import { FormContext } from '../../../../../context';
import { SharedIconCheck } from '../../../../shared/icons';

export const SustainabilityScanFormSections: React.FunctionComponent = () => {
  const intl = useIntl();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid, isDirty }
  } = useForm<SustainabilityScanFormFields>({
    mode: 'onBlur',
    defaultValues: getDefaultValues(intl)
  });

  const SustainabilityScanFormFieldsConfig = useSustainabilityScanFormFieldsConfig(intl);
  const { isLoading, hasError, setLoading, setError } = useContext(FormContext);
  const values = watch();
  const [isEnterPressed, setIsEnterPressed] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.code === 'Enter') {
      setIsEnterPressed(true);
    }
  };

  async function onSubmit(values: SustainabilityScanFormFields) {
    if (isEnterPressed) {
      setIsEnterPressed(false);
      return;
    }
    setLoading(true);

    const trimmedZipcode = values.postalCode.replace(/ +/g, '');
    window.location.href = `https://quickscan-hypotheker.smarttwin.nl/quickscan/select-address?zipcode=${trimmedZipcode}&housenumber=${
      values.houseNumber
    }${values.houseNumberAddition ? `&addition=${values.houseNumberAddition}` : ''}`;
  }

  return (
    <>
      <AppAlertDialog title={'let op!'} open={hasError} onClose={() => setError(false)}>
        <div>
          <FormattedMessage
            id={`sustainabilityScanForm.error`}
            defaultMessage={`Door een technische storing kunnen we nu helaas het formulier niet versturen.
          Probeer het opnieuw.`}
          />
        </div>
      </AppAlertDialog>

      <form noValidate={true} onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
        <div>
          <h1>
            <FormattedMessage
              id={`sustainabilityScanForm.title`}
              defaultMessage={`Online duurzaamheidsscan: bereken je besparing`}
            />
          </h1>
          <p>
            <FormattedMessage
              id={`sustainabilityScanForm.subTitle`}
              defaultMessage={`Met de vrijblijvende online scan van onze partner SmartTwin bereken je in een paar minuten hoeveel je kunt besparen met verschillende {link}.`}
              values={{
                link: (
                  <a
                    href={`https://www.hypotheker.nl/begrippenlijst/duurzaam-wonen/energiebesparende-voorzieningen-en-je-hypotheek/`}
                    target={`_blank`}
                    rel={`noopener noreferrer`}>
                    <FormattedMessage
                      id={`sustainabilityScanForm.subTitle.link`}
                      defaultMessage={`energiebesparende maatregelen`}
                    />
                  </a>
                )
              }}
            />
          </p>

          <ul className={`my-8 list-none`}>
            <li className={`mb-2`}>
              <SharedIconCheck color={`green.500`} boxSize={`8`} className={`mr-2`} />
              <FormattedMessage
                id={`sustainabilityScanForm.perks.explore`}
                defaultMessage={`Bekijk de verschillende mogelijkheden om je woning te verduurzamen.`}
              />
            </li>
            <li className={`mb-2`}>
              <SharedIconCheck color={`green.500`} boxSize={`8`} className={`mr-2`} />
              <FormattedMessage
                id={`sustainabilityScanForm.perks.calculate`}
                defaultMessage={`Bereken hoeveel je kunt besparen.`}
              />
            </li>
            <li className={`mb-2`}>
              <SharedIconCheck color={`green.500`} boxSize={`8`} className={`mr-2`} />
              <FormattedMessage
                id={`sustainabilityScanForm.perks.save`}
                defaultMessage={`Direct inzicht in de verbetering van je energielabel.`}
              />
            </li>
          </ul>
        </div>
        <div className={`md:w-4/5 my-4`}>
          <div>
            <FormElementDynamicInputTyped<SustainabilityScanFormFields>
              formFields={SustainabilityScanFormFieldsConfig}
              id={'postalCode'}
              register={register}
              errors={errors}
              values={values}
              validation={{
                validate: (value: string) => isValidZipcode(value, validationMessages.zipCode),
                required: validationMessages.required
              }}
            />
          </div>
          <div className={`md:flex md:gap-4`}>
            <div className={`md:w-1/2`}>
              <FormElementDynamicInputTyped<SustainabilityScanFormFields>
                formFields={SustainabilityScanFormFieldsConfig}
                id={'houseNumber'}
                register={register}
                errors={errors}
                values={values}
                options={{ required: true }}
              />
            </div>
            <div className={`md:w-1/2`}>
              <FormElementDynamicInputTyped<SustainabilityScanFormFields>
                formFields={SustainabilityScanFormFieldsConfig}
                id={'houseNumberAddition'}
                register={register}
                errors={errors}
                values={values}
              />
            </div>
          </div>
        </div>
        <div>
          <FormElementDynamicInputTyped<SustainabilityScanFormFields>
            formFields={SustainabilityScanFormFieldsConfig}
            id={'acceptShareDetails'}
            register={register}
            errors={errors}
            options={{ required: true }}
            values={values}
          />

          <FormElementDynamicInputTyped<SustainabilityScanFormFields>
            formFields={SustainabilityScanFormFieldsConfig}
            id={'acceptConditions'}
            register={register}
            errors={errors}
            options={{ required: true }}
            values={values}
          />
        </div>
        <Button
          className={`md:w-fit w-full mt-4`}
          isLoading={isLoading}
          loadingText={intl.formatMessage({
            id: 'sustainabilityScanForm.submitButton.loadingText',
            defaultMessage: 'Bezig met versturen'
          })}
          type="submit"
          colorScheme="orange"
          isDisabled={false}>
          {!isValid && isDirty
            ? intl.formatMessage({
                id: 'sustainabilityScanForm.submitButton.whatElse',
                defaultMessage: 'Wat moet ik nog invullen?'
              })
            : intl.formatMessage({
                id: 'sustainabilityScanForm.submitButton.label',
                defaultMessage: 'Versturen'
              })}
        </Button>
      </form>
    </>
  );
};
